import { LoadingIndicator } from "@/Icons/loadingIndicator";
import { trpc } from "@/Utils/trpc";
import { useSites } from "@/api/hooks/useSites";
import { Page } from "@/components/Page";
import { Button } from "@/components/button";
import { useTrans } from "@/translations";

import { FC, useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import ContextMenu from "./ContextMenu";
import { SiteLogoView } from "./SiteLogoView";

type GlobalSettingsProps = {};
type SiteData = {
  id: string;
  namespace: string;
  siteName: string | null;
  displayName: string | null;
};

type NewSite = {
  namespace: string;
  siteName: string;
  displayName: string;
};

const emptySite = {
  namespace: "",
  siteName: "",
  displayName: "",
};

export const GlobalSettings: FC<GlobalSettingsProps> = () => {
  const { t } = useTrans();
  const { sites, isLoading, refetch } = useSites();

  const { mutate, isPending: isUpdating } =
    trpc.config.updateSites.useMutation();
  const { mutate: createMutate, isPending: isCreating } =
    trpc.config.createSite.useMutation();
  const [siteData, setSiteData] = useState<SiteData[]>([]);

  const { register, watch, formState, reset } = useForm<NewSite>({
    defaultValues: emptySite,
  });

  useEffect(() => {
    if (sites) {
      setSiteData(sites);
    }
  }, [sites]);

  const handleDisplayNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, siteId: string) => {
      const updatedSiteData = siteData.map((site) => {
        if (site.id === siteId) {
          return {
            ...site,
            displayName: e.target.value,
          };
        }
        return site;
      });
      setSiteData(updatedSiteData);
    },
    [siteData, setSiteData],
  );

  const newSite = watch();

  const onSave = useCallback(() => {
    const sitesWithDisplayName = siteData.filter(
      (site) => !!site.displayName,
    ) as {
      id: string;
      displayName: string;
    }[];

    if (formState.isDirty) {
      if (newSite.namespace && newSite.siteName) {
        createMutate(newSite, {
          onSuccess: () => {
            refetch();
            reset(emptySite);
          },
        });
      }
    }
    mutate({
      sites: sitesWithDisplayName.map((site) => ({
        ...site,
        id: Number(site.id),
      })),
    });
  }, [
    siteData,
    mutate,
    newSite,
    formState.isDirty,
    createMutate,
    refetch,
    reset,
  ]);

  const isSaving = isUpdating || isCreating;

  if (isLoading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <LoadingIndicator />
      </div>
    );
  }

  return (
    <Page title="configLabels:settings">
      <div className={"bg-white py-4"}>
        <div className="px-4">
          <table className="w-full table-auto text-sm">
            <thead>
              <tr className="h-12 border-b text-left">
                <th>{t("configLabels:clientIdentifier")}</th>
                <th>{t("configLabels:siteName")}</th>
                <th>{t("configLabels:displayName")}</th>
                <th>{t("configLabels:logo")}</th>
              </tr>
            </thead>
            <tbody>
              {siteData?.map((site) => (
                <tr
                  key={site.id}
                  className="h-16 border-b last-of-type:border-none"
                >
                  <td>{site.namespace}</td>
                  <td>{site.siteName}</td>
                  <td>
                    <input
                      value={site.displayName || ""}
                      onChange={(e) => handleDisplayNameChange(e, site.id)}
                      data-1p-ignore
                    />
                  </td>
                  <td>
                    <SiteLogoView
                      displayName={
                        site.displayName || site.siteName || site.namespace
                      }
                      namespace={site.namespace}
                    />
                  </td>
                  <td className="relative mr-4 h-8 w-8">
                    <ContextMenu
                      siteName={site.displayName || site.siteName}
                      siteId={site.id}
                    />
                  </td>
                </tr>
              ))}
              <tr className="h-12 border-b last-of-type:border-none">
                <td>
                  <input {...register("namespace")} data-1p-ignore />
                </td>
                <td>
                  <input {...register("siteName")} data-1p-ignore />
                </td>
                <td>
                  <input {...register("displayName")} data-1p-ignore />
                </td>
              </tr>
            </tbody>
          </table>
          <div className="mt-8">
            <Button
              label={t("actions:save")}
              onClick={onSave}
              loading={isSaving}
            />
          </div>
        </div>
      </div>
    </Page>
  );
};
